import {
    SUPPORT_OVERLAY_ATTRIBUTE,
    type IAnnotation,
    type IRemoteCursor,
    type IRemoteMark,
} from '@srm/screen-sharing/light';
import { createEmitter, type IEmitter } from '@srm/ui/lib/valueStore';

/** Курсоры, рябь и пометки сотрудников: до 30 раз в секунду — мимо состояния React и контекста показа. */
export type SupportPresenceEvent =
    | { kind: 'cursor'; cursor: IRemoteCursor | null; peerId: string }
    | { kind: 'click'; mark: IRemoteMark; peerId: string }
    | { kind: 'annotations'; annotations: readonly IAnnotation[] };

export type ISupportPresenceBus = IEmitter<SupportPresenceEvent>;

export const createPresenceBus = (): ISupportPresenceBus => createEmitter<SupportPresenceEvent>();

/** Класс корня захвата (Element Capture): своё наложение контекста, плоский 3D — иначе браузер не захватит. */
export const SUPPORT_CAPTURE_CLASS = 'support-capture-root';

/**
 * Корень захвата — `body`: в нём и приложение, и порталы окон и меню. Оверлеи поддержки (курсор, рябь, кольцо)
 * рисуются в соседнем с `body` узле — в видео не попадают и эхом к сотруднику не возвращаются.
 */
export const supportCaptureRoot = (): HTMLElement => document.body;

const OVERLAY_ROOT_ID = 'support-overlay-root';

/**
 * Узел для оверлеев поддержки — ребёнок `<html>` после `body`, вне захватываемой области. Удалённое управление его не
 * трогает, а при Element Capture щелчок и колесо сотрудника в его месте уходят в страницу под ним (в видео его нет).
 */
export function supportOverlayRoot(): HTMLElement {
    let root = document.getElementById(OVERLAY_ROOT_ID);
    if (!root) {
        root = document.createElement('div');
        root.id = OVERLAY_ROOT_ID;
        root.className = 'support-overlay-root';
        root.setAttribute('data-remote-control-exclude', '');
        root.setAttribute(SUPPORT_OVERLAY_ATTRIBUTE, '');
        document.documentElement.append(root);
    }
    return root;
}

const POPUP_ROOT_ID = 'support-overlay-popups';

/**
 * Хост порталов всплывающих частей полосы показа (меню звука и громкости, «Посмотреть, что уйдёт», подсказки кнопок) —
 * внутри узла оверлеев, вне захвата: при Element Capture сотрудник их не видит. Сам узел мышь пропускает, его
 * дети — ловят.
 */
export function supportPopupRoot(): HTMLElement {
    const overlays = supportOverlayRoot();
    let root = document.getElementById(POPUP_ROOT_ID);
    if (!root || root.parentElement !== overlays) {
        root?.remove();
        root = document.createElement('div');
        root.id = POPUP_ROOT_ID;
        root.className = 'support-overlay-popups';
        overlays.append(root);
    }
    return root;
}

/** «Петров П. П.» → «Петров П.»: подпись курсора короче полосы. */
export const shortPeerName = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).join(' ');

/** Рябь клика держится, пока идёт её анимация. */
export const RIPPLE_DURATION = 800;
